"use client";

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DrawingPage from "./_components/DrawingPage";
import AudioPracticePage from "./_components/AudioPracticePage";
import AudioFeedback from "./_components/AudioFeedback";
import DrawingFeedback from "./_components/DrawingFeedback";
import type { DrawingBoardHandle } from "./_components/DrawingBoard";
import type { PracticeRecordingHandle } from "./_components/PracticeRecording";
import { useProfileContext } from "../../context/ProfileContext";
import {
  checkStudentAnswer,
  getStudentWordTaskStatus,
} from "../../services/backend/api";
import {
  readTeacherPracticeSession,
  saveTeacherPracticeSession,
  type TeacherPracticeSession,
  type TeacherPracticeStudent,
} from "../../services/teacherPracticeSession";
import { collectToyForWord } from "../../services/toyCollection";

export default function KidsDrawingApp() {
  const [currentPage, setCurrentPage] = useState("audio");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [audioRecordingPassed, setAudioRecordingPassed] = useState(false);
  const [englishAudioPassed, setEnglishAudioPassed] = useState(false);
  const [englishAudioFailed, setEnglishAudioFailed] = useState(false);
  const [teluguAudioPassed, setTeluguAudioPassed] = useState(false);
  const [teluguAudioFailed, setTeluguAudioFailed] = useState(false);
  const [showAudioFeedback, setShowAudioFeedback] = useState(false);
  const [hasSubmittedAudio, setHasSubmittedAudio] = useState(false);
  const [drawingPassed, setDrawingPassed] = useState(false);
  const [drawingFailed, setDrawingFailed] = useState(false);
  const [showDrawingFeedback, setShowDrawingFeedback] = useState(false);
  const [hasSubmittedDrawing, setHasSubmittedDrawing] = useState(false);
  const [teacherSession, setTeacherSession] = useState<TeacherPracticeSession | null>(null);
  const [isSwitchingStudent, setIsSwitchingStudent] = useState(false);
  const [recordedLanguage, setRecordedLanguage] = useState<"english" | "telugu">("english");

  const drawingRef = useRef<DrawingBoardHandle | null>(null);
  const recordingRef = useRef<PracticeRecordingHandle | null>(null);
  const rewardCollectedRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { currentWord, selectedProfile, getOrUpdateToNextWord, handleProfileSelect } =
    useProfileContext();
  const isTrackedStudent = (selectedProfile?.studentId ?? -1) > 0;
  const taskStatus = {
    english: englishAudioPassed,
    telugu: teluguAudioPassed,
    drawing: drawingPassed,
  };
  const taskProgressStatus = {
    english: englishAudioPassed ? "correct" : englishAudioFailed ? "wrong" : "pending",
    telugu: teluguAudioPassed ? "correct" : teluguAudioFailed ? "wrong" : "pending",
    drawing: drawingPassed ? "correct" : drawingFailed ? "wrong" : "pending",
  } as const;
  const selectedStudentId = selectedProfile?.studentId ?? -1;

  const goHome = () => {
    navigate(teacherSession?.fromTeacherDashboard ? "/teacher-dashboard" : "/profile");
  };

  const maybeCollectReward = async (nextTaskStatus: typeof taskStatus) => {
    const isEverythingComplete =
      nextTaskStatus.english && nextTaskStatus.telugu && nextTaskStatus.drawing;

    if (!isEverythingComplete || !isTrackedStudent || rewardCollectedRef.current) {
      return;
    }

    rewardCollectedRef.current = true;
    try {
      await collectToyForWord(
        selectedProfile?.studentId,
        currentWord?.category,
        currentWord?.english
      );
    } catch (error) {
      console.error("Reward collection failed:", error);
    }
  };

  const resetSession = ({ preserveRewardCollected = false } = {}) => {
    setShowAudioFeedback(false);
    setShowDrawingFeedback(false);
    setAudioRecordingPassed(false);
    setEnglishAudioPassed(false);
    setEnglishAudioFailed(false);
    setTeluguAudioPassed(false);
    setTeluguAudioFailed(false);
    setDrawingPassed(false);
    setHasSubmittedAudio(false);
    setHasSubmittedDrawing(false);
    setCurrentPage("audio");
    setUploadStatus("");
  };

  const moveToNextWord = () => {
    resetSession();
    getOrUpdateToNextWord();
  };

  const skipWord = async () => {
    if (currentWord && isTrackedStudent && (!hasSubmittedAudio || !hasSubmittedDrawing)) {
      const skipType = !hasSubmittedAudio ? "audio_english" : "image";
      try {
        await checkStudentAnswer({
          correct: null,
          studentId: selectedProfile?.studentId,
          difficulty: currentWord.difficulty,
          file: null,
          type: skipType,
          wordEnglish: currentWord.english,
          skip: true,
        });
      } catch (error) {
        console.error("Error saving skip attempt:", error);
      }
    }

    moveToNextWord();
  };

  const updateTeacherSessionStudent = (
    session: TeacherPracticeSession,
    studentId: number
  ) => {
    const updatedSession: TeacherPracticeSession = {
      ...session,
      currentStudentId: studentId,
    };
    setTeacherSession(updatedSession);
    saveTeacherPracticeSession(updatedSession);
  };

  const signInTeacherStudent = async (
    studentId: number,
    sourceSession?: TeacherPracticeSession | null
  ) => {
    const activeSession = sourceSession ?? teacherSession;
    if (!activeSession) return;

    const student = activeSession.students.find(
      (entry) => entry.studentId === studentId
    );
    if (!student) return;

    const profileLikeRecord: TeacherPracticeStudent = {
      studentId: student.studentId,
      studentName: student.studentName,
      classId: student.classId,
      section: student.section,
    };

    setIsSwitchingStudent(true);
    resetSession();
    try {
      await handleProfileSelect(profileLikeRecord, { navigateToProfile: false });
      updateTeacherSessionStudent(activeSession, student.studentId);
    } finally {
      setIsSwitchingStudent(false);
    }
  };

  useEffect(() => {
    const routeState = location.state as { fromTeacherDashboard?: boolean } | null;
    const storedSession = readTeacherPracticeSession();
    const isTeacherPractice =
      routeState?.fromTeacherDashboard ||
      (Boolean(localStorage.getItem("activeTeacher")) && Boolean(storedSession));

    if (!isTeacherPractice) {
      setTeacherSession(null);
      return;
    }

    if (!storedSession || storedSession.students.length === 0) {
      setTeacherSession(null);
      return;
    }

    setTeacherSession(storedSession);

    const selectedStudentId = selectedProfile?.studentId;
    const hasSelectedStudentInSession = storedSession.students.some(
      (student) => student.studentId === selectedStudentId
    );

    if (hasSelectedStudentInSession) {
      updateTeacherSessionStudent(storedSession, selectedStudentId);
      return;
    }

    const defaultStudentId =
      storedSession.currentStudentId ?? storedSession.students[0].studentId;
    void signInTeacherStudent(defaultStudentId, storedSession);
  }, []);

  useEffect(() => {
    const wordEnglish = currentWord?.english;
    if (!wordEnglish || !isTrackedStudent) {
      return;
    }

    let isCancelled = false;

    const loadPersistedTaskStatus = async () => {
      try {
        const savedStatus = await getStudentWordTaskStatus(
          selectedStudentId,
          wordEnglish
        );

        if (isCancelled) return;

        setEnglishAudioPassed(savedStatus.english === "correct");
        setEnglishAudioFailed(savedStatus.english === "wrong");
        setTeluguAudioPassed(savedStatus.telugu === "correct");
        setTeluguAudioFailed(savedStatus.telugu === "wrong");
        setDrawingPassed(savedStatus.drawing === "correct");
        setDrawingFailed(savedStatus.drawing === "wrong");
        rewardCollectedRef.current =
          savedStatus.english === "correct" &&
          savedStatus.telugu === "correct" &&
          savedStatus.drawing === "correct";
      } catch (error) {
        console.error("Could not load saved word progress:", error);
      }
    };

    void loadPersistedTaskStatus();

    return () => {
      isCancelled = true;
    };
  }, [currentWord?.english, isTrackedStudent, selectedStudentId]);

  const handleSubmitEnglishAudio = async () => {
    setIsUploading(true);
    const audioBlob = await recordingRef.current?.getEnglishAudioBlob();
    if (!audioBlob || !currentWord) {
      setUploadStatus("❌ No English audio recorded. Please record before submitting.");
      setIsUploading(false);
      return;
    }

    try {
      const hasPassed = await checkStudentAnswer({
        correct: null,
        studentId: selectedProfile?.studentId,
        difficulty: currentWord!.difficulty,
        file: audioBlob,
        type: "audio_english",
        wordEnglish: currentWord!.english,
        skip: false,
      });

      setHasSubmittedAudio(true);
      setAudioRecordingPassed(hasPassed);
      const nextEnglishPassed = englishAudioPassed || hasPassed;
      setEnglishAudioPassed(nextEnglishPassed);
      setEnglishAudioFailed(!nextEnglishPassed);

      const nextTaskStatus = {
        english: nextEnglishPassed,
        telugu: teluguAudioPassed,
        drawing: drawingPassed,
      };
      await maybeCollectReward(nextTaskStatus);

      if (nextTaskStatus.english && nextTaskStatus.telugu && nextTaskStatus.drawing) {
        setShowAudioFeedback(false);
        setShowDrawingFeedback(true);
      } else {
        setShowAudioFeedback(true);
      }
    } catch (error) {
      console.error("Error checking student answer:", error);
      setUploadStatus("❌ Oops! Could not submit audio. Try again later.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmitTeluguAudio = async () => {
    setIsUploading(true);
    const audioBlob = await recordingRef.current?.getTeluguAudioBlob();
    if (!audioBlob || !currentWord) {
      setUploadStatus("❌ No Telugu audio recorded. Please record before submitting.");
      setIsUploading(false);
      return;
    }

    try {
      const hasPassed = await checkStudentAnswer({
        correct: null,
        studentId: selectedProfile?.studentId,
        difficulty: currentWord!.difficulty,
        file: audioBlob,
        type: "audio_telugu",
        wordEnglish: currentWord!.english,
        skip: false,
      });

      setAudioRecordingPassed(hasPassed);
      const nextTeluguPassed = teluguAudioPassed || hasPassed;
      setTeluguAudioPassed(nextTeluguPassed);
      setTeluguAudioFailed(!nextTeluguPassed);

      const nextTaskStatus = {
        english: englishAudioPassed,
        telugu: nextTeluguPassed,
        drawing: drawingPassed,
      };
      await maybeCollectReward(nextTaskStatus);

      if (nextTaskStatus.english && nextTaskStatus.telugu && nextTaskStatus.drawing) {
        setShowAudioFeedback(false);
        setShowDrawingFeedback(true);
      } else {
        setShowAudioFeedback(true);
      }
    } catch (error) {
      console.error("Error checking Telugu answer:", error);
      setUploadStatus("❌ Oops! Could not submit audio. Try again later.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmitAudio = async () => {
    const teluguBlob = await recordingRef.current?.getTeluguAudioBlob();
    const englishBlob = await recordingRef.current?.getEnglishAudioBlob();

    if (teluguBlob) {
      setRecordedLanguage("telugu");
      await handleSubmitTeluguAudio();
    } else if (englishBlob) {
      setRecordedLanguage("english");
      await handleSubmitEnglishAudio();
    } else {
      setUploadStatus("❌ No audio recorded. Please record before submitting.");
    }
  };

  const handleSubmitDrawing = async () => {
    setIsUploading(true);
    try {
      const imageBlob = await drawingRef.current?.getImageBlob();

      if (!imageBlob) {
        setUploadStatus("❌ No drawing made. Please draw before sharing.");
        return;
      }

      const hasPassed = await checkStudentAnswer({
        correct: null,
        studentId: selectedProfile?.studentId,
        difficulty: currentWord!.difficulty,
        file: imageBlob,
        type: "image",
        wordEnglish: currentWord!.english,
        skip: false,
      });

      const nextDrawingPassed = drawingPassed || hasPassed;
      setDrawingFailed(!nextDrawingPassed);
      const nextTaskStatus = {
        english: englishAudioPassed,
        telugu: teluguAudioPassed,
        drawing: nextDrawingPassed,
      };
      await maybeCollectReward(nextTaskStatus);

      setHasSubmittedDrawing(true);
      setDrawingPassed(hasPassed);
      setShowDrawingFeedback(true);
      setUploadStatus("✅ Drawing submitted successfully!");
    } catch (err) {
      console.error("Drawing submission error:", err);
      setUploadStatus("❌ Oops! Could not submit drawing. Try again later.");
    } finally {
      setIsUploading(false);
    }
  };

  if (showDrawingFeedback) {
    const shouldShowRewardAnimation =
      taskStatus.english && taskStatus.telugu && taskStatus.drawing;
    const shouldShowToyCollection = shouldShowRewardAnimation && isTrackedStudent;

    return (
      <DrawingFeedback
        isSuccess={drawingPassed}
        taskStatus={taskStatus}
        progressStatus={taskProgressStatus}
        showRewardAnimation={shouldShowRewardAnimation}
        showToyCollection={shouldShowToyCollection}
        onSkip={skipWord}
        onNextWord={moveToNextWord}
        onDrawAgain={() => {
          setShowDrawingFeedback(false);
        }}
      />
    );
  }

  if (showAudioFeedback) {
    return (
      <AudioFeedback
        isSuccess={audioRecordingPassed}
        onSkip={skipWord}
        tryAgain={() => setShowAudioFeedback(false)}
        goToDrawingPage={() => {
          setCurrentPage("drawing");
          setShowAudioFeedback(false);
        }}
        recordedLanguage={recordedLanguage}
        taskStatus={taskProgressStatus}
        onPracticeOther={() => {
          setShowAudioFeedback(false);
          setCurrentPage("audio");
        }}
      />
    );
  }

  if (currentPage === "audio") {
    return (
      <AudioPracticePage
        recordingRef={recordingRef}
        onSkip={skipWord}
        onSubmitAudio={handleSubmitAudio}
        isUploading={isUploading}
        uploadStatus={uploadStatus}
        isTeacherSession={Boolean(teacherSession)}
        teacherStudents={teacherSession?.students ?? []}
        activeStudentId={selectedProfile?.studentId ?? teacherSession?.currentStudentId ?? null}
        isSwitchingStudent={isSwitchingStudent}
        onTeacherStudentChange={(studentId) => { void signInTeacherStudent(studentId); }}
        onHome={goHome}
        taskStatus={taskProgressStatus}
      />
    );
  }

  return (
    <DrawingPage
      drawingRef={drawingRef}
      onBack={() => setCurrentPage("audio")}
      onSubmit={handleSubmitDrawing}
      onSkip={skipWord}
      isUploading={isUploading}
      uploadStatus={uploadStatus}
      taskStatus={taskProgressStatus}
      onHome={goHome}
    />
  );
}
